import type { StructureType } from '@territory-nexus/shared'
import { STRUCTURE_META } from '@territory-nexus/shared'
import clsx from 'clsx'

// Individual sprite paths
const STRUCTURE_SPRITES: Record<StructureType, string> = {
  laser_turret: '/assets/structures/structure-laser-turret.png',
  shield_generator: '/assets/structures/structure-shield-generator.png',
  stealth_field: '/assets/structures/structure-stealth-field.png',
  mining_rig: '/assets/structures/structure-mining-rig.png',
  research_lab: '/assets/structures/structure-research-lab.png',
  headquarters: '/assets/structures/structure-headquarters.png',
  teleport_gate: '/assets/structures/structure-teleport-gate.png',
}

interface StructureSpriteProps {
  structureType: StructureType
  level: number
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showLevel?: boolean
  className?: string
}

const SIZES = {
  sm: 40,
  md: 56,
  lg: 80,
  xl: 112,
}

export default function StructureSprite({
  structureType,
  level,
  size = 'md',
  showLevel = true,
  className,
}: StructureSpriteProps) {
  const spriteUrl = STRUCTURE_SPRITES[structureType]
  const pixelSize = SIZES[size]
  const meta = STRUCTURE_META[structureType]

  const glowColor = meta.glowColor
  const glowIntensity = Math.min(level / 10, 1)

  return (
    <div
      className={clsx(
        'relative inline-flex items-center justify-center',
        className
      )}
      style={{ width: pixelSize, height: pixelSize }}
    >
      {/* Glow effect layer */}
      <div
        className="absolute inset-0 rounded-lg transition-all duration-300"
        style={{
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
          opacity: 0.25 + glowIntensity * 0.2,
          transform: `scale(${1.1 + glowIntensity * 0.15})`,
          filter: 'blur(6px)',
        }}
      />

      {/* Scanline sweep */}
      <div
        className="absolute inset-0 z-[5] pointer-events-none overflow-hidden rounded-lg"
        style={{ opacity: 0.12 }}
      >
        <div
          className="absolute inset-0 animate-scan"
          style={{
            background: `linear-gradient(transparent 40%, ${glowColor} 50%, transparent 60%)`,
            height: '200%',
          }}
        />
      </div>

      {/* Sprite image */}
      <img
        src={spriteUrl}
        alt={meta.name}
        className="relative z-10 object-contain"
        style={{
          width: pixelSize,
          height: pixelSize,
          filter: `brightness(1.05) saturate(1.25) drop-shadow(0 0 3px ${glowColor})`,
        }}
      />

      {/* Level badge */}
      {showLevel && level > 1 && (
        <div
          className={clsx(
            'absolute -bottom-1 -right-1 z-20 rounded-full flex items-center justify-center text-[10px] font-bold',
            'bg-slate-900 border text-white',
            level < 5 && 'border-slate-500',
            level >= 5 && 'border-cyan-500 text-cyan-400',
            level >= 8 && 'border-amber-500 text-amber-400',
            level >= 10 && 'border-rose-500 text-rose-400 animate-pulse'
          )}
          style={{
            width: Math.max(16, pixelSize * 0.35),
            height: Math.max(16, pixelSize * 0.35),
            fontSize: Math.max(8, pixelSize * 0.2),
          }}
        >
          {level}
        </div>
      )}
    </div>
  )
}
